import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import type { SortDescriptor } from 'react-aria-components';
import {
    Upload01,
    SearchLg,
    Eye,
    Trash01,
    XCircle,
    Play,
    X,
    CheckCircle,
    AlertCircle,
    FileSearch01,
    Tag01,
    Copy01,
    Link01,
    LayoutGrid01,
    List,
    FolderClosed,
    ArrowLeft,
    SwitchHorizontal01,
    RefreshCcw05,
    Edit05,
    Database01,
} from '@untitledui/icons';
import { toast as sonnerToast } from 'sonner';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { useTranslation } from 'react-i18next';
import { useTrainingDocuments, useCreateTrainingDocument, useDeleteTrainingDocument, useUpdateTrainingDocument, useBatchParsing, useBatchClassification, useBatchReassignType, useDeleteTrainingDocumentsBatch, useExtractionStrategies, useScanGCS } from '@/hooks/api/use-document-training';
import { useDocumentSchemas } from '@/hooks/api/use-document-schemas';
import { useDocumentTypes } from '@/hooks/api/use-document-types';
import { useSubmitBatchOcr, useBatchOcrStatus } from '@/hooks/api/use-batch-ocr';
import type { DocumentLabTrainingResponse, DocumentLabTrainingListFilters } from '@/types/api/document-training';
import { TrainingStatus as TrainingStatusEnum } from '@/types/api/document-training';
import { BatchOCRJobStatus } from '@/types/api/batch-ocr';
import { useToast } from '@/hooks/use-toast';

export const TrainingDocumentsPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const [viewMode, setViewMode] = useState<'folders' | 'table'>('folders');
    const [folderGroupBy, setFolderGroupBy] = useState<'type' | 'dataset'>('type');
    const [folderTypeFilter, setFolderTypeFilter] = useState<string | null>(null);
    const [folderDatasetFilter, setFolderDatasetFilter] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({ column: 'updated_at', direction: 'descending' });
    const [documentTypeFilter, setDocumentTypeFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<TrainingStatusEnum | ''>('');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [docToDeleteId, setDocToDeleteId] = useState<string | null>(null);

    // Multi-select state
    const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

    // Batch OCR state
    const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
    const progressToastId = useRef<string | number | null>(null);

    // Batch actions modals state
    const [isStrategyModalOpen, setIsStrategyModalOpen] = useState(false);
    const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);
    const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
    const [reassignTypeId, setReassignTypeId] = useState<string>('');
    const [selectedBatchStrategy, setSelectedBatchStrategy] = useState<string>('vision');

    // Folder action state
    const [folderToDelete, setFolderToDelete] = useState<{ typeKey: string; typeName: string; docIds: string[] } | null>(null);
    const [folderToRename, setFolderToRename] = useState<{ typeKey: string; typeName: string; docIds: string[]; currentDataset: string } | null>(null);
    const [newDatasetName, setNewDatasetName] = useState('');
    const [folderToReassign, setFolderToReassign] = useState<{ typeKey: string; typeName: string; docIds: string[] } | null>(null);
    const [reassignFolderTypeId, setReassignFolderTypeId] = useState<string>('');

    // Error retry tracking for polling
    const consecutiveErrorsRef = useRef<number>(0);
    const prevIsErrorRef = useRef<boolean>(false);
    const MAX_CONSECUTIVE_ERRORS = 2;

    const [uploadFormData, setUploadFormData] = useState<{
        document_type_id: string;
        document_schema_id: string;
        dataset_name: string;
        dataset_version: string;
        notes: string;
    }>({
        document_type_id: '',
        document_schema_id: '',
        dataset_name: 'default',
        dataset_version: '1.0',
        notes: '',
    });

    // API hooks
    const { data: documentTypes = [] } = useDocumentTypes({ limit: 100 });

    // Find selected document type to get document_type key for schemas API
    const selectedDocumentType = useMemo(() => {
        return documentTypes.find(dt => dt.document_type_id === uploadFormData.document_type_id);
    }, [documentTypes, uploadFormData.document_type_id]);

    const filters: DocumentLabTrainingListFilters = useMemo(() => ({
        training_status: statusFilter ? (statusFilter as TrainingStatusEnum) : undefined,
        limit: 500,
    }), [statusFilter]);

    const { data: allDocuments = [], isLoading, isError, refetch } = useTrainingDocuments(filters, {
        refetchInterval: () => {
            // Se abbiamo raggiunto il massimo di errori consecutivi, interrompi il polling
            if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
                return false;
            }
            // Altrimenti continua il polling ogni 10 secondi
            return 10000;
        },
    });

    const { data: availableSchemas = [] } = useDocumentSchemas({
        document_type: selectedDocumentType?.key || undefined,
        is_active: true,
        limit: 500,
    });

    const createTrainingDocument = useCreateTrainingDocument();
    const deleteTrainingDocument = useDeleteTrainingDocument();
    const updateTrainingDocument = useUpdateTrainingDocument();
    const scanGCS = useScanGCS();

    // Batch actions hooks
    const batchParsing = useBatchParsing();
    const batchClassification = useBatchClassification();
    const batchReassignType = useBatchReassignType();
    const batchDelete = useDeleteTrainingDocumentsBatch();
    const { data: extractionStrategies = [] } = useExtractionStrategies();

    // Batch OCR hooks
    const submitBatchOcr = useSubmitBatchOcr();
    const { data: batchStatus } = useBatchOcrStatus(activeBatchId || undefined, {
        pollingEnabled: !!activeBatchId,
        pollingInterval: 7000,
    });

    // Resolve document type key for a doc (same logic as folder grouping)
    const resolveDocTypeKey = useCallback((doc: DocumentLabTrainingResponse) => {
        if (doc.document_type) return doc.document_type;
        if (doc.document_type_id) {
            const dt = documentTypes.find(d => d.document_type_id === doc.document_type_id);
            if (dt) return dt.key;
        }
        return '';
    }, [documentTypes]);

    // Filter documents by type/dataset and search term (client-side filtering)
    const documents = useMemo(() => {
        let filtered = allDocuments;

        // Filter by document type (client-side to handle docs with null document_type)
        if (documentTypeFilter) {
            if (documentTypeFilter === '__unclassified__') {
                filtered = filtered.filter(doc => !resolveDocTypeKey(doc));
            } else {
                filtered = filtered.filter(doc => resolveDocTypeKey(doc) === documentTypeFilter);
            }
        }

        // Filter by dataset name (when navigating from dataset folder)
        if (folderDatasetFilter) {
            filtered = filtered.filter(doc => (doc.dataset_name || 'default') === folderDatasetFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (doc) =>
                    doc.file_path.toLowerCase().includes(term) ||
                    doc.dataset_name.toLowerCase().includes(term) ||
                    doc.notes?.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [allDocuments, documentTypeFilter, folderDatasetFilter, searchTerm, resolveDocTypeKey]);

    // Sorted documents for table view
    const sortedDocuments = useMemo(() => {
        if (!sortDescriptor.column) return documents;

        return [...documents].sort((a, b) => {
            let aVal: string | number = '';
            let bVal: string | number = '';

            switch (sortDescriptor.column) {
                case 'file_name':
                    aVal = (a.file_path.split('/').pop() || a.file_path).toLowerCase();
                    bVal = (b.file_path.split('/').pop() || b.file_path).toLowerCase();
                    break;
                case 'document_type':
                    aVal = (documentTypes.find(dt => dt.key === a.document_type)?.name || a.document_type || '').toLowerCase();
                    bVal = (documentTypes.find(dt => dt.key === b.document_type)?.name || b.document_type || '').toLowerCase();
                    break;
                case 'schema':
                    aVal = (a.detected_schema_name || '').toLowerCase();
                    bVal = (b.detected_schema_name || '').toLowerCase();
                    break;
                case 'dataset_name':
                    aVal = (a.dataset_name || '').toLowerCase();
                    bVal = (b.dataset_name || '').toLowerCase();
                    break;
                case 'training_status':
                    aVal = (a.training_status || '').toLowerCase();
                    bVal = (b.training_status || '').toLowerCase();
                    break;
                case 'created_at':
                    aVal = new Date(a.created_at).getTime();
                    bVal = new Date(b.created_at).getTime();
                    break;
                case 'updated_at':
                    aVal = new Date(a.updated_at).getTime();
                    bVal = new Date(b.updated_at).getTime();
                    break;
                default:
                    return 0;
            }

            const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return sortDescriptor.direction === 'descending' ? -comparison : comparison;
        });
    }, [documents, sortDescriptor, documentTypes]);

    // Folder grouping for folder view (by type or dataset)
    const folderGroups = useMemo(() => {
        const groups: Record<string, {
            groupKey: string;
            groupName: string;
            count: number;
            docIds: string[];
            schemas: Record<string, number>;
            datasets: Record<string, number>;
            types: Record<string, number>;
            statuses: Record<string, number>;
        }> = {};

        for (const doc of allDocuments) {
            const typeKey = resolveDocTypeKey(doc) || '__unclassified__';
            const datasetName = doc.dataset_name || 'default';

            // Determine grouping key based on mode
            const key = folderGroupBy === 'type' ? typeKey : datasetName;
            let groupName = '';
            if (folderGroupBy === 'type') {
                if (key !== '__unclassified__') {
                    const dt = documentTypes.find(d => d.key === key);
                    if (dt) groupName = dt.name;
                }
                groupName = groupName || (key === '__unclassified__' ? t('training.folders.unclassified', { defaultValue: 'Unclassified' }) : key);
            } else {
                groupName = datasetName;
            }

            if (!groups[key]) {
                groups[key] = {
                    groupKey: key,
                    groupName,
                    count: 0,
                    docIds: [],
                    schemas: {},
                    datasets: {},
                    types: {},
                    statuses: {},
                };
            }
            groups[key].count++;
            groups[key].docIds.push(doc.training_doc_id);

            const schemaName = doc.detected_schema_name || t('training.folders.noSchema', { defaultValue: 'No schema' });
            groups[key].schemas[schemaName] = (groups[key].schemas[schemaName] || 0) + 1;

            groups[key].datasets[datasetName] = (groups[key].datasets[datasetName] || 0) + 1;

            const typeName = documentTypes.find(d => d.key === typeKey)?.name || typeKey;
            groups[key].types[typeName] = (groups[key].types[typeName] || 0) + 1;

            const status = doc.training_status || 'unknown';
            groups[key].statuses[status] = (groups[key].statuses[status] || 0) + 1;
        }

        return Object.values(groups).sort((a, b) => b.count - a.count);
    }, [allDocuments, documentTypes, t, resolveDocTypeKey, folderGroupBy]);

    // Folder click handler
    const handleFolderClick = useCallback((groupKey: string) => {
        if (folderGroupBy === 'type') {
            setDocumentTypeFilter(groupKey);
            setFolderTypeFilter(groupKey);
            setFolderDatasetFilter(null);
        } else {
            setFolderDatasetFilter(groupKey);
            setFolderTypeFilter(null);
            setDocumentTypeFilter('');
        }
        setViewMode('table');
    }, [folderGroupBy]);

    // Back to folders handler
    const handleBackToFolders = useCallback(() => {
        setDocumentTypeFilter('');
        setFolderTypeFilter(null);
        setFolderDatasetFilter(null);
        setViewMode('folders');
    }, []);

    // Multi-select handler
    const handleClearSelection = useCallback(() => {
        setSelectedDocIds(new Set());
    }, []);

    // Document type options from API
    const documentTypeOptions = useMemo(() => [
        { id: '', label: t('training.documentTypePlaceholder') },
        ...documentTypes
            .filter(dt => dt.is_active !== false)
            .map((dt) => ({ id: dt.document_type_id, label: dt.name })),
    ], [documentTypes, t]);

    // Document type options by key (for filter)
    const documentTypeFilterOptions = useMemo(() => [
        { id: '', label: t('training.documentTypePlaceholder') },
        ...documentTypes
            .filter(dt => dt.is_active !== false)
            .map((dt) => ({ id: dt.key, label: dt.name })),
    ], [documentTypes, t]);

    const statusOptions = [
        { id: '', label: t('training.statusPlaceholder') },
        { id: TrainingStatusEnum.PENDING, label: t('training.status.pending') },
        { id: TrainingStatusEnum.IN_PROGRESS, label: t('training.status.inProgress', { defaultValue: 'In Progress' }) },
        { id: TrainingStatusEnum.ANNOTATED, label: t('training.status.annotated', { defaultValue: 'Annotato' }) },
        { id: TrainingStatusEnum.VALIDATED, label: t('training.status.validated', { defaultValue: 'Validato' }) },
        { id: TrainingStatusEnum.APPROVED, label: t('training.status.approved', { defaultValue: 'Approvato' }) },
        { id: TrainingStatusEnum.REJECTED, label: t('training.status.rejected', { defaultValue: 'Rifiutato' }) },
    ];

    useEffect(() => {
        // Reset schema selection if current schema is not available for new document type
        if (uploadFormData.document_schema_id && !availableSchemas.find(s => s.document_schema_id === uploadFormData.document_schema_id)) {
            setUploadFormData({ ...uploadFormData, document_schema_id: '' });
        }
    }, [uploadFormData.document_type_id, availableSchemas]);

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        setUploadFiles([...uploadFiles, ...files]);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            setUploadFiles([...uploadFiles, ...files]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadFiles(uploadFiles.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (uploadFiles.length === 0) {
            toast.error(t('common.selectAtLeastOneFile', { defaultValue: 'Seleziona almeno un file' }));
            return;
        }

        // Document type is now optional - removed validation

        try {
            for (const file of uploadFiles) {
                const progressKey = file.name;
                setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));

                const result = await createTrainingDocument.mutateAsync({
                    file,
                    data: {
                        dataset_name: uploadFormData.dataset_name,
                        dataset_version: uploadFormData.dataset_version,
                        // Pass document_type only if selected (key)
                        ...(selectedDocumentType?.key && { document_type: selectedDocumentType.key }),
                        // Pass document_type_id at top level for backend
                        ...(uploadFormData.document_type_id && { document_type_id: uploadFormData.document_type_id }),
                        notes: uploadFormData.notes || null,
                        // Keep schema_id in attribute_metadata for additional context
                        attribute_metadata: {
                            document_schema_id: uploadFormData.document_schema_id || null,
                        },
                    },
                });

                // Show duplicate filename warnings
                if (result?.warnings?.length) {
                    for (const warning of result.warnings) {
                        toast.warning(warning);
                    }
                }

                setUploadProgress((prev) => ({ ...prev, [progressKey]: 100 }));
            }

            toast.success(t('training.messages.uploadSuccess', { defaultValue: 'Documenti caricati con successo' }));
            setUploadFiles([]);
            setUploadProgress({});
            setIsUploadModalOpen(false);
            // Reset form
            setUploadFormData({
                document_type_id: '',
                document_schema_id: '',
                dataset_name: 'default',
                dataset_version: '1.0',
                notes: '',
            });
            refetch();
        } catch (error) {
            console.error('Error uploading training documents:', error);
            toast.error(t('training.messages.uploadError', { defaultValue: 'Errore nel caricamento dei documenti' }));
        }
    };

    const handleOpenDetails = (doc: DocumentLabTrainingResponse) => {
        navigate(`/admin/documents/training/${doc.training_doc_id}`, { state: navigationStateFrom(location) });
    };

    const handleDelete = (id: string) => {
        setDocToDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (!docToDeleteId) return;

        try {
            await deleteTrainingDocument.mutateAsync(docToDeleteId);
            toast.success(t('training.messages.deleteSuccess', { defaultValue: 'Documento eliminato con successo' }));
            setDocToDeleteId(null);
            refetch();
        } catch (error) {
            console.error('Error deleting training document:', error);
            toast.error(t('training.messages.deleteError', { defaultValue: 'Errore nell\'eliminazione del documento' }));
        }
    };

    const handleCancelDelete = () => {
        setDocToDeleteId(null);
    };

    // Batch OCR handler
    const handleBatchOcr = async () => {
        if (selectedDocIds.size === 0) return;

        try {
            const result = await submitBatchOcr.mutateAsync({
                training_document_ids: Array.from(selectedDocIds),
                batch_name: `Batch ${new Date().toLocaleString('it-IT')}`,
            });

            setActiveBatchId(result.batch_job_id);
            handleClearSelection();

            // Show initial toast
            toast.info(t('training.batchOcr.messages.submitted', {
                defaultValue: 'Batch OCR avviato per {{count}} documenti',
                count: selectedDocIds.size
            }));
        } catch (error) {
            console.error('Error submitting batch OCR:', error);
            toast.error(t('training.batchOcr.messages.submitError', {
                defaultValue: 'Errore nell\'avvio del batch OCR'
            }));
        }
    };

    // Batch Parsing handler
    const handleBatchParsing = async () => {
        if (selectedDocIds.size === 0) return;

        try {
            await batchParsing.mutateAsync({
                training_document_ids: Array.from(selectedDocIds),
                strategy: selectedBatchStrategy,
            });

            handleClearSelection();
            setIsStrategyModalOpen(false);

            toast.success(t('training.batchActions.messages.parsingSubmitted', {
                defaultValue: 'Parsing avviato per {{count}} documenti',
                count: selectedDocIds.size
            }));
            refetch();
        } catch (error) {
            console.error('Error submitting batch parsing:', error);
            toast.error(t('training.batchActions.messages.parsingError', {
                defaultValue: 'Errore nell\'avvio del batch parsing'
            }));
        }
    };

    // Batch Classification handler
    const handleBatchClassification = async () => {
        if (selectedDocIds.size === 0) return;

        try {
            await batchClassification.mutateAsync({
                training_document_ids: Array.from(selectedDocIds),
            });

            handleClearSelection();

            toast.success(t('training.batchActions.messages.classificationSubmitted', {
                defaultValue: 'Classificazione avviata per {{count}} documenti',
                count: selectedDocIds.size
            }));
            refetch();
        } catch (error) {
            console.error('Error submitting batch classification:', error);
            toast.error(t('training.batchActions.messages.classificationError', {
                defaultValue: 'Errore nell\'avvio della classificazione batch'
            }));
        }
    };

    // Batch Reassign Type handler
    const handleBatchReassignType = async () => {
        if (selectedDocIds.size === 0 || !reassignTypeId) return;

        try {
            const result = await batchReassignType.mutateAsync({
                training_document_ids: Array.from(selectedDocIds),
                document_type_id: reassignTypeId,
            });

            handleClearSelection();
            setIsReassignModalOpen(false);
            setReassignTypeId('');

            toast.success(t('training.batchActions.messages.reassignSuccess', {
                defaultValue: '{{count}} documenti assegnati a {{type}}',
                count: result.success,
                type: result.document_type
            }));
            refetch();
        } catch (error) {
            console.error('Error batch reassigning type:', error);
            toast.error(t('training.batchActions.messages.reassignError', {
                defaultValue: 'Errore nell\'assegnazione del tipo documento'
            }));
        }
    };

    // Batch Delete handler
    const handleBatchDelete = async () => {
        if (selectedDocIds.size === 0) return;

        try {
            const result = await batchDelete.mutateAsync(Array.from(selectedDocIds));

            handleClearSelection();
            setIsBatchDeleteModalOpen(false);

            toast.success(t('training.batchActions.messages.deleteSuccess', {
                defaultValue: '{{count}} documenti eliminati',
                count: result.deleted_count
            }));
            refetch();
        } catch (error) {
            console.error('Error deleting batch documents:', error);
            toast.error(t('training.batchActions.messages.deleteError', {
                defaultValue: 'Errore nell\'eliminazione dei documenti'
            }));
        }
    };

    // Folder delete handler
    const handleFolderDelete = async () => {
        if (!folderToDelete) return;
        try {
            const result = await batchDelete.mutateAsync(folderToDelete.docIds);
            toast.success(`Deleted ${result.deleted_count} documents from "${folderToDelete.typeName}"`);
            setFolderToDelete(null);
            refetch();
        } catch (error) {
            console.error('Error deleting folder:', error);
            toast.error('Error deleting folder documents');
        }
    };

    // Folder rename dataset handler
    const handleFolderRenameDataset = async () => {
        if (!folderToRename || !newDatasetName.trim()) return;
        try {
            let updated = 0;
            for (const docId of folderToRename.docIds) {
                await updateTrainingDocument.mutateAsync({ docId, data: { dataset_name: newDatasetName.trim() } });
                updated++;
            }
            toast.success(`Updated dataset name to "${newDatasetName.trim()}" for ${updated} documents`);
            setFolderToRename(null);
            setNewDatasetName('');
            refetch();
        } catch (error) {
            console.error('Error renaming dataset:', error);
            toast.error('Error updating dataset name');
        }
    };

    // Folder reassign type handler
    const handleFolderReassignType = async () => {
        if (!folderToReassign || !reassignFolderTypeId) return;
        try {
            const result = await batchReassignType.mutateAsync({
                training_document_ids: folderToReassign.docIds,
                document_type_id: reassignFolderTypeId,
            });
            toast.success(`Reassigned ${result.success} documents to new type`);
            setFolderToReassign(null);
            setReassignFolderTypeId('');
            refetch();
        } catch (error) {
            console.error('Error reassigning folder type:', error);
            toast.error('Error reassigning document type');
        }
    };

    // Progress toast effect
    useEffect(() => {
        if (!batchStatus || !activeBatchId) return;

        const progress = batchStatus.total_documents > 0
            ? ((batchStatus.completed_documents + batchStatus.failed_documents) / batchStatus.total_documents) * 100
            : 0;

        const isTerminal = [
            BatchOCRJobStatus.COMPLETED,
            BatchOCRJobStatus.PARTIALLY_COMPLETED,
            BatchOCRJobStatus.FAILED,
        ].includes(batchStatus.status as BatchOCRJobStatus);

        // Update or create progress toast
        const toastContent = (
            <div className="flex flex-col gap-2 min-w-[280px]">
                <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                        {t('training.batchOcr.progressTitle', { defaultValue: 'Batch OCR' })}
                    </span>
                    {isTerminal && (
                        <button
                            onClick={() => {
                                if (progressToastId.current) {
                                    sonnerToast.dismiss(progressToastId.current);
                                }
                                setActiveBatchId(null);
                                progressToastId.current = null;
                            }}
                            className="text-tertiary hover:text-primary"
                        >
                            <X className="size-4" />
                        </button>
                    )}
                </div>
                <ProgressBar value={progress} labelPosition="right" />
                <div className="flex items-center justify-between text-xs text-tertiary">
                    <span>
                        {batchStatus.completed_documents + batchStatus.failed_documents}/{batchStatus.total_documents}
                    </span>
                    <div className="flex items-center gap-1">
                        {batchStatus.status === BatchOCRJobStatus.COMPLETED && (
                            <>
                                <CheckCircle className="size-3 text-success" />
                                <span className="text-success">{t('training.batchOcr.status.completed', { defaultValue: 'Completato' })}</span>
                            </>
                        )}
                        {batchStatus.status === BatchOCRJobStatus.PARTIALLY_COMPLETED && (
                            <>
                                <AlertCircle className="size-3 text-warning" />
                                <span className="text-warning">{t('training.batchOcr.status.partiallyCompleted', { defaultValue: 'Parziale' })}</span>
                            </>
                        )}
                        {batchStatus.status === BatchOCRJobStatus.FAILED && (
                            <>
                                <XCircle className="size-3 text-error" />
                                <span className="text-error">{t('training.batchOcr.status.failed', { defaultValue: 'Fallito' })}</span>
                            </>
                        )}
                        {(batchStatus.status === BatchOCRJobStatus.SUBMITTED || batchStatus.status === BatchOCRJobStatus.IN_PROGRESS) && (
                            <>
                                <div className="size-3 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                                <span>{t('training.batchOcr.status.processing', { defaultValue: 'In corso...' })}</span>
                            </>
                        )}
                    </div>
                </div>
                {batchStatus.failed_documents > 0 && (
                    <div className="text-xs text-error">
                        {t('training.batchOcr.failedCount', {
                            defaultValue: '{{count}} documenti falliti',
                            count: batchStatus.failed_documents
                        })}
                    </div>
                )}
            </div>
        );

        if (progressToastId.current) {
            // Update existing toast
            sonnerToast.custom(toastContent as any, {
                id: progressToastId.current,
                duration: isTerminal ? 10000 : Infinity,
            });
        } else {
            // Create new toast
            progressToastId.current = sonnerToast.custom(toastContent as any, {
                duration: Infinity,
            });
        }

        // Cleanup when terminal and auto-dismiss after delay
        if (isTerminal) {
            refetch(); // Refresh document list
            const timer = setTimeout(() => {
                if (progressToastId.current) {
                    sonnerToast.dismiss(progressToastId.current);
                }
                setActiveBatchId(null);
                progressToastId.current = null;
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [batchStatus, activeBatchId, t, refetch]);

    // Clear selection when filters change
    useEffect(() => {
        setSelectedDocIds(new Set());
    }, [documentTypeFilter, statusFilter, searchTerm]);

    // Reset error counter when filters change (nuova query = nuovo tentativo)
    useEffect(() => {
        consecutiveErrorsRef.current = 0;
        prevIsErrorRef.current = false;
    }, [documentTypeFilter, statusFilter]);

    // Track consecutive errors for polling interruption
    useEffect(() => {
        if (isError) {
            // Incrementa solo se è un nuovo errore (non durante il primo render)
            if (!prevIsErrorRef.current) {
                consecutiveErrorsRef.current += 1;
            }
            prevIsErrorRef.current = true;
        } else if (!isLoading && prevIsErrorRef.current) {
            // Reset counter quando la query passa da errore a successo
            consecutiveErrorsRef.current = 0;
            prevIsErrorRef.current = false;
        } else if (!isLoading && !isError) {
            // Reset anche quando la query ha successo (anche se non c'era errore precedente)
            prevIsErrorRef.current = false;
        }
    }, [isError, isLoading]);

    const getStatusBadge = (status: TrainingStatusEnum) => {
        switch (status) {
            case TrainingStatusEnum.PENDING:
                return <Badge color="gray" size="sm">{t('training.status.pending')}</Badge>;
            case TrainingStatusEnum.IN_PROGRESS:
                return <Badge color="warning" size="sm">{t('training.status.inProgress', { defaultValue: 'In Progress' })}</Badge>;
            case TrainingStatusEnum.ANNOTATED:
                return <Badge color="blue" size="sm">{t('training.status.annotated', { defaultValue: 'Annotato' })}</Badge>;
            case TrainingStatusEnum.VALIDATED:
                return <Badge color="blue" size="sm">{t('training.status.validated', { defaultValue: 'Validato' })}</Badge>;
            case TrainingStatusEnum.APPROVED:
                return <Badge color="success" size="sm">{t('training.status.approved', { defaultValue: 'Approvato' })}</Badge>;
            case TrainingStatusEnum.REJECTED:
                return <Badge color="error" size="sm">{t('training.status.rejected', { defaultValue: 'Rifiutato' })}</Badge>;
            default:
                return <Badge color="gray" size="sm">{status}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-error mb-4">{t('training.messages.loadError', { defaultValue: 'Errore nel caricamento dei documenti' })}</p>
                    <Button onClick={() => refetch()} size="md" color="primary">
                        {t('common.retry', { defaultValue: 'Riprova' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('training.title')}</h2>
                    <p className="text-tertiary mt-1">{t('training.description')}</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center rounded-lg border border-secondary">
                        <button
                            onClick={() => {
                                setViewMode('folders');
                                if (folderTypeFilter || folderDatasetFilter) {
                                    setDocumentTypeFilter('');
                                    setFolderTypeFilter(null);
                                    setFolderDatasetFilter(null);
                                }
                            }}
                            className={`p-2 rounded-l-lg transition-colors ${viewMode === 'folders' ? 'bg-active text-primary' : 'text-tertiary hover:text-primary'}`}
                            title={t('training.viewMode.folders', { defaultValue: 'Folder view' })}
                        >
                            <LayoutGrid01 className="size-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`p-2 rounded-r-lg transition-colors ${viewMode === 'table' ? 'bg-active text-primary' : 'text-tertiary hover:text-primary'}`}
                            title={t('training.viewMode.table', { defaultValue: 'Table view' })}
                        >
                            <List className="size-4" />
                        </button>
                    </div>
                    {viewMode === 'folders' && (
                        <div className="flex items-center rounded-lg border border-secondary">
                            <button
                                onClick={() => setFolderGroupBy('type')}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-l-lg text-xs font-medium transition-colors ${folderGroupBy === 'type' ? 'bg-active text-primary' : 'text-tertiary hover:text-primary'}`}
                                title="Group by document type"
                            >
                                <Tag01 className="size-3.5" />
                                Type
                            </button>
                            <button
                                onClick={() => setFolderGroupBy('dataset')}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-r-lg text-xs font-medium transition-colors ${folderGroupBy === 'dataset' ? 'bg-active text-primary' : 'text-tertiary hover:text-primary'}`}
                                title="Group by dataset"
                            >
                                <Database01 className="size-3.5" />
                                Dataset
                            </button>
                        </div>
                    )}
                    <Button
                        onClick={() => {
                            scanGCS.mutate({}, {
                                onSuccess: (data) => {
                                    toast.success(
                                        `Sync complete: ${data.newly_created} new, ${data.already_in_db} already tracked`,
                                        { description: `${data.total_files_found} files found in GCS${data.skipped ? `, ${data.skipped} skipped` : ''}` }
                                    );
                                },
                                onError: (error) => {
                                    toast.error(error);
                                },
                            });
                        }}
                        size="md"
                        color="secondary"
                        iconLeading={RefreshCcw05}
                        disabled={scanGCS.isPending}
                    >
                        {scanGCS.isPending ? t('loading', { ns: 'common', defaultValue: 'Loading...' }) : t('training.syncGCS', { defaultValue: 'Sync GCS' })}
                    </Button>
                    <Button
                        onClick={() => setIsUploadModalOpen(true)}
                        size="md"
                        color="primary"
                        iconLeading={Upload01}
                    >
                        {t('training.uploadDocuments')}
                    </Button>
                </div>
            </div>

            {/* Folder View */}
            {viewMode === 'folders' && (
                <>
                    {folderGroups.length === 0 ? (
                        <div className="text-center py-12">
                            <FolderClosed className="size-12 mx-auto mb-4 text-tertiary" />
                            <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                            <p className="text-tertiary">{t('noData.message')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {folderGroups.map((folder) => (
                                <div
                                    key={folder.groupKey}
                                    className="text-left rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary hover:ring-brand hover:shadow-md transition-all group"
                                >
                                    <button
                                        onClick={() => handleFolderClick(folder.groupKey)}
                                        className="w-full text-left"
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            {folderGroupBy === 'type'
                                                ? <FolderClosed className="size-5 text-brand flex-shrink-0 mt-0.5" />
                                                : <Database01 className="size-5 text-brand flex-shrink-0 mt-0.5" />
                                            }
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-medium text-primary truncate group-hover:text-brand transition-colors">
                                                    {folder.groupName}
                                                </h3>
                                                <p className="text-sm text-tertiary">
                                                    {t('training.folders.documentCount', {
                                                        defaultValue: '{{count}} documents',
                                                        count: folder.count
                                                    })}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Schema badges */}
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {Object.entries(folder.schemas)
                                                .sort(([, a], [, b]) => b - a)
                                                .slice(0, 3)
                                                .map(([schema, count]) => (
                                                    <span
                                                        key={schema}
                                                        className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-xs text-tertiary"
                                                    >
                                                        {schema}: {count}
                                                    </span>
                                                ))}
                                            {Object.keys(folder.schemas).length > 3 && (
                                                <span className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-xs text-tertiary">
                                                    +{Object.keys(folder.schemas).length - 3}
                                                </span>
                                            )}
                                        </div>

                                        {/* Secondary info: datasets when grouped by type, types when grouped by dataset (hidden in dataset view) */}
                                        {folderGroupBy === 'type' && (
                                            <div className="flex flex-wrap gap-1 mb-3">
                                                {Object.entries(folder.datasets)
                                                    .sort(([, a], [, b]) => b - a)
                                                    .slice(0, 3)
                                                    .map(([label, count]) => (
                                                        <span
                                                            key={label}
                                                            className="inline-flex items-center rounded-md bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700"
                                                        >
                                                            {label}: {count}
                                                        </span>
                                                    ))}
                                                {Object.keys(folder.datasets).length > 3 && (
                                                    <span className="inline-flex items-center rounded-md bg-brand-50 px-1.5 py-0.5 text-xs text-brand-700">
                                                        +{Object.keys(folder.datasets).length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                    {/* Status dots */}
                                    <div className="flex items-center gap-3 text-xs">
                                        {folder.statuses[TrainingStatusEnum.PENDING] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-yellow-400" />
                                                {folder.statuses[TrainingStatusEnum.PENDING]}
                                            </span>
                                        )}
                                        {folder.statuses[TrainingStatusEnum.IN_PROGRESS] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-orange-400" />
                                                {folder.statuses[TrainingStatusEnum.IN_PROGRESS]}
                                            </span>
                                        )}
                                        {folder.statuses[TrainingStatusEnum.ANNOTATED] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-blue-400" />
                                                {folder.statuses[TrainingStatusEnum.ANNOTATED]}
                                            </span>
                                        )}
                                        {folder.statuses[TrainingStatusEnum.VALIDATED] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-blue-500" />
                                                {folder.statuses[TrainingStatusEnum.VALIDATED]}
                                            </span>
                                        )}
                                        {folder.statuses[TrainingStatusEnum.APPROVED] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-green-500" />
                                                {folder.statuses[TrainingStatusEnum.APPROVED]}
                                            </span>
                                        )}
                                        {folder.statuses[TrainingStatusEnum.REJECTED] && (
                                            <span className="flex items-center gap-1 text-tertiary">
                                                <span className="size-2 rounded-full bg-red-500" />
                                                {folder.statuses[TrainingStatusEnum.REJECTED]}
                                            </span>
                                        )}
                                    </div>
                                    </button>

                                    {/* Folder actions */}
                                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-secondary">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const mainDataset = Object.entries(folder.datasets).sort(([, a], [, b]) => b - a)[0]?.[0] || 'default';
                                                setFolderToRename({ typeKey: folder.groupKey, typeName: folder.groupName, docIds: folder.docIds, currentDataset: mainDataset });
                                                setNewDatasetName(mainDataset);
                                            }}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-tertiary hover:text-primary hover:bg-secondary transition-colors"
                                            title="Rename dataset"
                                        >
                                            <Edit05 className="size-3.5" />
                                            Dataset
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFolderToDelete({ typeKey: folder.groupKey, typeName: folder.groupName, docIds: folder.docIds });
                                            }}
                                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-tertiary hover:text-error-600 hover:bg-error-50 transition-colors ml-auto"
                                            title="Delete all documents in folder"
                                        >
                                            <Trash01 className="size-3.5" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
                <>
                    {/* Breadcrumb when navigated from folder */}
                    {(folderTypeFilter || folderDatasetFilter) && (
                        <div className="flex items-center gap-2 text-sm">
                            <button
                                onClick={handleBackToFolders}
                                className="flex items-center gap-1 text-tertiary hover:text-primary transition-colors"
                            >
                                <ArrowLeft className="size-4" />
                                {t('training.folders.allFolders', { defaultValue: 'All Folders' })}
                            </button>
                            <span className="text-tertiary">/</span>
                            <span className="text-primary font-medium">
                                {folderGroups.find(f => f.groupKey === (folderTypeFilter || folderDatasetFilter))?.groupName || folderTypeFilter || folderDatasetFilter}
                                {' '}({documents.length})
                            </span>
                        </div>
                    )}

                    {/* Filters */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <Input
                                placeholder={t('training.searchPlaceholder')}
                                value={searchTerm}
                                onChange={setSearchTerm}
                                icon={SearchLg}
                                size="md"
                            />

                            <Select.ComboBox
                                placeholder={t('training.documentTypeSearchPlaceholder', { defaultValue: 'Cerca tipo documento...' })}
                                items={documentTypeFilterOptions}
                                selectedKey={documentTypeFilter}
                                onSelectionChange={(key) => setDocumentTypeFilter(key ? String(key) : '')}
                                size="md"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select.ComboBox>

                            <Select
                                placeholder={t('training.statusPlaceholder')}
                                items={statusOptions}
                                selectedKey={statusFilter}
                                onSelectionChange={(key) => setStatusFilter(key ? (key as TrainingStatusEnum) : '')}
                                size="md"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                    </div>

                    {/* Action Bar - shown when documents are selected */}
                    {selectedDocIds.size > 0 && (
                        <div className="sticky top-0 z-10 flex items-center justify-between rounded-xl bg-brand-solid p-4 shadow-md">
                            <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-white">
                                    {t('training.batchOcr.selected', {
                                        defaultValue: '{{count}} selezionati',
                                        count: selectedDocIds.size
                                    })}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Button
                                    onClick={handleBatchOcr}
                                    size="sm"
                                    color="primary"
                                    iconLeading={Play}
                                    isDisabled={submitBatchOcr.isPending || activeBatchId !== null}
                                >
                                    {submitBatchOcr.isPending
                                        ? t('loading')
                                        : t('training.batchOcr.runOcr', { defaultValue: 'Avvia OCR' })}
                                </Button>
                                <Button
                                    onClick={() => setIsStrategyModalOpen(true)}
                                    size="sm"
                                    color="primary"
                                    iconLeading={FileSearch01}
                                    isDisabled={batchParsing.isPending}
                                >
                                    {batchParsing.isPending
                                        ? t('loading')
                                        : t('training.batchActions.runParsing', { defaultValue: 'Avvia Parsing' })}
                                </Button>
                                <Button
                                    onClick={handleBatchClassification}
                                    size="sm"
                                    color="primary"
                                    iconLeading={Tag01}
                                    isDisabled={batchClassification.isPending}
                                >
                                    {batchClassification.isPending
                                        ? t('loading')
                                        : t('training.batchActions.runClassification', { defaultValue: 'Classifica' })}
                                </Button>
                                <Button
                                    onClick={() => setIsReassignModalOpen(true)}
                                    size="sm"
                                    color="secondary"
                                    iconLeading={SwitchHorizontal01}
                                    isDisabled={batchReassignType.isPending}
                                >
                                    {batchReassignType.isPending
                                        ? t('loading')
                                        : t('training.batchActions.reassignType', { defaultValue: 'Assegna Tipo' })}
                                </Button>
                                <Button
                                    onClick={() => setIsBatchDeleteModalOpen(true)}
                                    size="sm"
                                    color="secondary-destructive"
                                    iconLeading={Trash01}
                                    isDisabled={batchDelete.isPending}
                                >
                                    {batchDelete.isPending
                                        ? t('loading')
                                        : t('training.batchActions.delete', { defaultValue: 'Elimina' })}
                                </Button>
                                <Button
                                    onClick={handleClearSelection}
                                    size="sm"
                                    color="secondary"
                                >
                                    {t('training.batchOcr.clearSelection', { defaultValue: 'Annulla selezione' })}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Documents Table */}
                    <TableCard.Root>
                        <TableCard.Header
                            title={`${t('training.title')} (${documents.length})`}
                            description={t('training.description')}
                        />

                        <Table
                            selectionBehavior="toggle"
                            selectionMode="multiple"
                            selectedKeys={selectedDocIds}
                            sortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            onSelectionChange={(keys) => {
                                if (keys === 'all') {
                                    setSelectedDocIds(new Set(sortedDocuments.map(doc => doc.training_doc_id)));
                                } else {
                                    setSelectedDocIds(keys as Set<string>);
                                }
                            }}
                        >
                            <Table.Header>
                                <Table.Head id="file_name" label={t('training.headers.fileName')} allowsSorting isRowHeader />
                                <Table.Head id="document_type" label={t('training.headers.documentType')} allowsSorting />
                                <Table.Head id="schema" label="Schema" allowsSorting />
                                <Table.Head id="dataset_name" label="Dataset" allowsSorting />
                                <Table.Head id="training_status" label={t('training.headers.status')} allowsSorting />
                                <Table.Head id="created_at" label={t('training.headers.uploadDate')} allowsSorting />
                                <Table.Head id="updated_at" label="Last Update" allowsSorting />
                                <Table.Head label={t('training.headers.actions')} />
                            </Table.Header>
                            <Table.Body>
                                {sortedDocuments.map((doc) => (
                                    <Table.Row key={doc.training_doc_id} id={doc.training_doc_id}>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                {doc.is_split_segment && (
                                                    <Link01 className="size-4 text-tertiary flex-shrink-0" />
                                                )}
                                                <div className="font-medium text-primary">{doc.file_path.split('/').pop() || doc.file_path}</div>
                                                {(doc.split_count ?? 0) > 0 && (
                                                    <Badge color="brand" size="sm" className="flex items-center gap-1">
                                                        <Copy01 className="size-3" />
                                                        {t('training.splitIndicator.containsSplits', {
                                                            defaultValue: '{{count}} splits',
                                                            count: doc.split_count
                                                        })}
                                                    </Badge>
                                                )}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color="gray" size="sm">
                                                {documentTypes.find((dt) => dt.key === doc.document_type)?.name || doc.document_type || '—'}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {doc.detected_schema_name || '—'}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {doc.dataset_name || '—'}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            {getStatusBadge(doc.training_status as TrainingStatusEnum)}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {new Date(doc.created_at).toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {new Date(doc.updated_at).toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    onClick={() => handleOpenDetails(doc)}
                                                    size="sm"
                                                    color="secondary"
                                                    iconLeading={Eye}
                                                >
                                                    {t('training.actions.viewDetails')}
                                                </Button>
                                                <Button
                                                    onClick={() => handleDelete(doc.training_doc_id)}
                                                    size="sm"
                                                    color="secondary-destructive"
                                                    iconLeading={Trash01}
                                                >
                                                    {t('training.actions.delete')}
                                                </Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>

                    {documents.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-tertiary mb-4">
                                <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                                <p className="text-tertiary">{t('noData.message')}</p>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={docToDeleteId !== null} onOpenChange={(open) => !open && handleCancelDelete()}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={handleCancelDelete}
                                        size="md"
                                        color="secondary"
                                        disabled={deleteTrainingDocument.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDelete}
                                        size="md"
                                        color="secondary-destructive"
                                        disabled={deleteTrainingDocument.isPending}
                                        iconLeading={Trash01}
                                    >
                                        {deleteTrainingDocument.isPending ? t('loading') : t('training.actions.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Folder Delete Confirmation Modal */}
            <DialogTrigger isOpen={folderToDelete !== null} onOpenChange={(open) => !open && setFolderToDelete(null)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-2">
                                    Delete Folder
                                </h2>
                                <p className="text-tertiary mb-6">
                                    This will permanently delete all <strong>{folderToDelete?.docIds.length}</strong> documents
                                    in "{folderToDelete?.typeName}" including their files from cloud storage. This action cannot be undone.
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => setFolderToDelete(null)}
                                        size="md"
                                        color="secondary"
                                        disabled={batchDelete.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleFolderDelete}
                                        size="md"
                                        color="secondary-destructive"
                                        disabled={batchDelete.isPending}
                                        iconLeading={Trash01}
                                    >
                                        {batchDelete.isPending ? 'Deleting...' : `Delete ${folderToDelete?.docIds.length} documents`}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Folder Rename Dataset Modal */}
            <DialogTrigger isOpen={folderToRename !== null} onOpenChange={(open) => !open && setFolderToRename(null)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-2">
                                    Rename Dataset
                                </h2>
                                <p className="text-tertiary mb-4">
                                    Change the dataset name for all <strong>{folderToRename?.docIds.length}</strong> documents
                                    in "{folderToRename?.typeName}".
                                </p>
                                <div className="mb-6">
                                    <Input
                                        label="Dataset name"
                                        value={newDatasetName}
                                        onChange={(value) => setNewDatasetName(value)}
                                        placeholder="e.g. training_v2"
                                    />
                                </div>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => { setFolderToRename(null); setNewDatasetName(''); }}
                                        size="md"
                                        color="secondary"
                                        disabled={updateTrainingDocument.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleFolderRenameDataset}
                                        size="md"
                                        color="primary"
                                        disabled={updateTrainingDocument.isPending || !newDatasetName.trim() || newDatasetName.trim() === folderToRename?.currentDataset}
                                        iconLeading={Edit05}
                                    >
                                        {updateTrainingDocument.isPending ? 'Updating...' : 'Rename'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Folder Reassign Type Modal */}
            <DialogTrigger isOpen={folderToReassign !== null} onOpenChange={(open) => !open && setFolderToReassign(null)}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-2">
                                    Change Document Type
                                </h2>
                                <p className="text-tertiary mb-4">
                                    Reassign all <strong>{folderToReassign?.docIds.length}</strong> documents
                                    from "{folderToReassign?.typeName}" to a new document type.
                                </p>
                                <div className="mb-6">
                                    <Select.ComboBox
                                        selectedKey={reassignFolderTypeId}
                                        onSelectionChange={(key) => setReassignFolderTypeId(key as string)}
                                        placeholder="Search document type..."
                                        shortcut={false}
                                        items={documentTypes
                                            .filter(dt => dt.is_active !== false && dt.key !== folderToReassign?.typeKey)
                                            .map((dt) => ({
                                                id: dt.document_type_id,
                                                label: dt.name,
                                                supportingText: dt.key,
                                            }))}
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                <div className="font-medium">{item.label}</div>
                                                <div className="text-xs text-tertiary">{item.supportingText}</div>
                                            </Select.Item>
                                        )}
                                    </Select.ComboBox>
                                </div>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => { setFolderToReassign(null); setReassignFolderTypeId(''); }}
                                        size="md"
                                        color="secondary"
                                        disabled={batchReassignType.isPending}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleFolderReassignType}
                                        size="md"
                                        color="primary"
                                        disabled={batchReassignType.isPending || !reassignFolderTypeId}
                                        iconLeading={SwitchHorizontal01}
                                    >
                                        {batchReassignType.isPending ? 'Reassigning...' : 'Reassign'}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Upload Modal */}
            <DialogTrigger isOpen={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {t('training.uploadDocuments')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select.ComboBox
                                            label={t('training.modal.fields.documentType')}
                                            items={documentTypeOptions}
                                            selectedKey={uploadFormData.document_type_id}
                                            onSelectionChange={(key) =>
                                                setUploadFormData({ ...uploadFormData, document_type_id: key ? String(key) : '', document_schema_id: '' })
                                            }
                                            placeholder={t('training.modal.fields.documentTypeSearch', { defaultValue: 'Cerca tipo documento...' })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select.ComboBox>

                                        <Select.ComboBox
                                            label={t('training.modal.fields.schema', { defaultValue: 'Schema' })}
                                            items={availableSchemas.map(s => ({ id: s.document_schema_id, label: s.schema_name }))}
                                            selectedKey={uploadFormData.document_schema_id}
                                            onSelectionChange={(key) =>
                                                setUploadFormData({ ...uploadFormData, document_schema_id: key ? String(key) : '' })
                                            }
                                            placeholder={t('training.modal.fields.schemaSearch', { defaultValue: 'Cerca schema...' })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select.ComboBox>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('training.modal.fields.datasetName')}
                                            value={uploadFormData.dataset_name}
                                            onChange={(value) => setUploadFormData({ ...uploadFormData, dataset_name: value })}
                                            isRequired
                                            size="md"
                                        />

                                        <Input
                                            label={t('training.modal.fields.datasetVersion')}
                                            value={uploadFormData.dataset_version}
                                            onChange={(value) => setUploadFormData({ ...uploadFormData, dataset_version: value })}
                                            size="md"
                                        />
                                    </div>

                                    {/* File Upload Area */}
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-brand bg-brand/10' : 'border-secondary'
                                            }`}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <Upload01 className="size-12 mx-auto mb-4 text-tertiary" />
                                        <p className="text-primary mb-2">{t('training.uploadArea')}</p>
                                        <p className="text-sm text-tertiary mb-4">{t('training.supportedFormats')}</p>
                                        <Button
                                            onClick={() => fileInputRef.current?.click()}
                                            size="md"
                                            color="secondary"
                                        >
                                            {t('common.selectFile')}
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                    </div>

                                    {/* Selected Files */}
                                    {uploadFiles.length > 0 && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">{t('common.selectedFiles')}</label>
                                            <div className="space-y-1">
                                                {uploadFiles.map((file, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between rounded-lg bg-secondary p-2"
                                                    >
                                                        <span className="text-sm text-primary">{file.name}</span>
                                                        <div className="flex items-center gap-2">
                                                            {uploadProgress[file.name] !== undefined && (
                                                                <div className="w-24 h-2 bg-secondary rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-brand transition-all"
                                                                        style={{ width: `${uploadProgress[file.name]}%` }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <button
                                                                onClick={() => handleRemoveFile(index)}
                                                                className="text-error hover:text-error_hover"
                                                            >
                                                                <XCircle className="size-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={() => {
                                            setIsUploadModalOpen(false);
                                            setUploadFiles([]);
                                            setUploadProgress({});
                                            setUploadFormData({
                                                document_type_id: '',
                                                document_schema_id: '',
                                                dataset_name: 'default',
                                                dataset_version: '1.0',
                                                notes: '',
                                            });
                                        }}
                                        size="md"
                                        color="secondary"
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleUpload}
                                        size="md"
                                        color="primary"
                                        iconLeading={Upload01}
                                        isDisabled={createTrainingDocument.isPending || uploadFiles.length === 0}
                                    >
                                        {createTrainingDocument.isPending ? t('loading') : t('common.upload')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Strategy Selection Modal for Batch Parsing */}
            <DialogTrigger isOpen={isStrategyModalOpen} onOpenChange={setIsStrategyModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('training.batchActions.strategyModal.title', { defaultValue: 'Seleziona Strategia di Parsing' })}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('training.batchActions.strategyModal.description', {
                                        defaultValue: 'Scegli la strategia di estrazione da utilizzare per {{count}} documenti.',
                                        count: selectedDocIds.size
                                    })}
                                </p>

                                <div className="mb-6">
                                    <Select
                                        label={t('training.batchActions.strategyModal.strategyLabel', { defaultValue: 'Strategia' })}
                                        items={extractionStrategies.map(s => ({
                                            id: s.name,
                                            label: s.name,
                                            description: s.description
                                        }))}
                                        selectedKey={selectedBatchStrategy}
                                        onSelectionChange={(key) => setSelectedBatchStrategy(key ? String(key) : 'vision')}
                                        size="md"
                                    >
                                        {(item) => (
                                            <Select.Item key={item.id} id={item.id} textValue={item.label}>
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{item.label}</span>
                                                    {item.description && (
                                                        <span className="text-xs text-tertiary">{item.description}</span>
                                                    )}
                                                </div>
                                            </Select.Item>
                                        )}
                                    </Select>
                                </div>

                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => setIsStrategyModalOpen(false)}
                                        size="md"
                                        color="secondary"
                                        disabled={batchParsing.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleBatchParsing}
                                        size="md"
                                        color="primary"
                                        disabled={batchParsing.isPending}
                                        iconLeading={FileSearch01}
                                    >
                                        {batchParsing.isPending ? t('loading') : t('training.batchActions.runParsing', { defaultValue: 'Avvia Parsing' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Batch Delete Confirmation Modal */}
            {/* Reassign Type Modal */}
            <DialogTrigger isOpen={isReassignModalOpen} onOpenChange={setIsReassignModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('training.batchActions.reassignModal.title', { defaultValue: 'Assegna Tipo Documento' })}
                                </h2>
                                <p className="text-tertiary mb-4">
                                    {t('training.batchActions.reassignModal.description', {
                                        defaultValue: 'Seleziona il tipo documento da assegnare a {{count}} documenti.',
                                        count: selectedDocIds.size
                                    })}
                                </p>
                                <Select
                                    label={t('training.batchActions.reassignModal.typeLabel', { defaultValue: 'Tipo Documento' })}
                                    items={documentTypes.map(dt => ({ id: dt.document_type_id, label: dt.name }))}
                                    selectedKey={reassignTypeId}
                                    onSelectionChange={(key) => setReassignTypeId(key as string)}
                                    size="md"
                                    placeholder={t('training.batchActions.reassignModal.typePlaceholder', { defaultValue: 'Seleziona tipo...' })}
                                >
                                    {(item) => (
                                        <Select.Item key={item.id} id={item.id}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </Select>
                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button
                                        onClick={() => { setIsReassignModalOpen(false); setReassignTypeId(''); }}
                                        size="md"
                                        color="secondary"
                                        disabled={batchReassignType.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleBatchReassignType}
                                        size="md"
                                        color="primary"
                                        disabled={batchReassignType.isPending || !reassignTypeId}
                                        iconLeading={SwitchHorizontal01}
                                    >
                                        {batchReassignType.isPending
                                            ? t('loading')
                                            : t('training.batchActions.reassignModal.confirm', { defaultValue: 'Assegna' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Batch Delete Modal */}
            <DialogTrigger isOpen={isBatchDeleteModalOpen} onOpenChange={setIsBatchDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('training.batchActions.deleteModal.title', { defaultValue: 'Conferma Eliminazione' })}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('training.batchActions.deleteModal.description', {
                                        defaultValue: 'Sei sicuro di voler eliminare {{count}} documenti? Questa azione non può essere annullata.',
                                        count: selectedDocIds.size
                                    })}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button
                                        onClick={() => setIsBatchDeleteModalOpen(false)}
                                        size="md"
                                        color="secondary"
                                        disabled={batchDelete.isPending}
                                    >
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleBatchDelete}
                                        size="md"
                                        color="secondary-destructive"
                                        disabled={batchDelete.isPending}
                                        iconLeading={Trash01}
                                    >
                                        {batchDelete.isPending ? t('loading') : t('training.batchActions.delete', { defaultValue: 'Elimina' })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

        </div>
    );
};
